import {
  Theme,
  createTheme as createMuiTheme,
  ThemeOptions,
} from '@material-ui/core';
import colors from './colors';

const formLabelColor = colors.silver;

const options: ThemeOptions = {
  overrides: {
    MuiBackdrop: {
      root: {
        background: 'rgba(5, 16, 35, 0.8)',
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiIconButton: {
      root: {
        color: colors.madison,
        padding: 0,
      },
    },

    MuiLink: {
      root: {
        color: colors.bright_turquoise,
      },
    },

    MuiButton: {
      containedPrimary: {
        backgroundColor: colors.bright_turquoise,
        color: colors.white,
        '&:hover': {
          backgroundColor: colors.bright_turquoise,
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
        },
        '&:disabled': {
          backgroundColor: colors.gallery,
          color: colors.silver,
        },
        '&:active': {
          backgroundColor: colors.madison,
        },
      },
      outlinedPrimary: {
        color: colors.bright_turquoise,
        borderColor: colors.bright_turquoise,
        '&:hover': {
          borderColor: colors.bright_turquoise,
          backgroundColor: 'rgba(0, 194, 255, 0.12)',
        },
        '&:disabled': {
          backgroundColor: 'transparent',
          border: `1px solid ${colors.silver}`,
          color: colors.silver,
        },
        '&:active': {
          backgroundColor: 'rgba(23, 73, 160, 0.12)',
          color: colors.bright_turquoise,
          border: `1px solid ${colors.madison}`,
        },
      },
      containedSecondary: {
        backgroundColor: colors.blue_zodiac,
        color: colors.shamrock,
        '&:hover': {
          backgroundColor: colors.blue_zodiac,
        },
      },
    },

    MuiSnackbarContent: {
      root: {
        backgroundColor: colors.blue_zodiac,
        color: colors.white,
      },
    },

    MuiAppBar: {
      colorPrimary: {
        backgroundColor: colors.onahau,
        color: colors.blue_zodiac,
      },
    },

    MuiTypography: {
      // Fallback to blue color
      root: {
        color: colors.madison,
      },

      // Possible text colors, example of how to set:
      // <Typography color='primary|secondary'>My text</Typography>
      colorPrimary: { color: colors.madison },
      colorSecondary: { color: colors.abbey },

      // No name in design system
      h1: {
        fontWeight: 900,
        fontSize: 32,
        lineHeight: '48px',
      },

      // Design system: Headline
      h2: {
        fontWeight: 800,
        fontSize: 22,
        lineHeight: '33px',
      },

      // Design system: Title
      h3: {
        fontWeight: 500,
        fontSize: 20,
        lineHeight: '30px',
      },

      // Design system: Subheader
      h4: {
        fontWeight: 'normal',
        fontSize: 14,
        lineHeight: '20px',
      },

      // Design system: Body1
      body1: {
        fontWeight: 800,
        fontSize: 16,
        lineHeight: '24px',
      },

      // Design system: Body2
      body2: {
        fontWeight: 600,
        fontSize: 16,
        lineHeight: '24px',
      },

      // Design system: Caption
      caption: {
        fontWeight: 'normal',
        fontSize: 12,
        lineHeight: '18px',
      },

      h6: {
        fontWeight: 800,
        color: colors.madison,
        fontSize: 16,
      },
    },
    MuiFormControlLabel: {
      root: {
        '&$focused': {
          color: colors.madison,
        },
      },
    },
    MuiInputLabel: {
      root: {
        '&$focused': {
          color: formLabelColor,
        },
      },
      formControl: {
        left: 10,
      },
      shrink: {
        transform: 'translate(0, 1.5px) scale(1.0)',
        color: formLabelColor,
        fontSize: 12,
        '&.Mui-disabled': {
          color: colors.silver,
        },
      },
    },
    MuiFormLabel: {
      root: {
        color: formLabelColor,
        '&.Mui-focused': {
          color: formLabelColor,
        },
        '&.Mui-error': {
          color: `${colors.bright_pink}`,
        },
        '&.Mui-disabled': {
          color: colors.silver,
        },
      },
    },
    MuiFormHelperText: {
      root: {
        color: `${colors.abbey}`,
        '&:error': {
          color: `${colors.bright_turquoise}`,
          backgroundColor: `${colors.bright_turquoise}`,
        },
        '&.MuiFormHelperText-root.Mui-error': {
          color: `${colors.bright_pink}`,
        },
        error: {},
      },
    },
    MuiInputAdornment: {
      root: {
        color: colors.madison,
      },
    },
    MuiSelect: {
      root: {
        '&.MuiSelect-select:focus': {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiRadio: {
      root: {
        color: `${colors.bright_turquoise} !important`,
      },
    },
    MuiFormControl: {
      root: {
        paddingLeft: 10,
        paddingRight: 10,
      },
    },
    MuiInput: {
      root: {
        color: colors.madison,
        fontWeight: 600,
        fontSize: 16,
        lineHeight: '24px',
        height: 37,
      },
      underline: {
        '&.MuiInput-underline.Mui-error:after': {
          transform: 'scaleX(1)',
          borderBottomColor: `${colors.bright_pink}`,
        },
        '&:before': {
          // For iOS mobile, removes square icon
          content: "''",
          marginLeft: -10,
          marginRight: -10,
          transition:
            'border - bottom - color 200ms cubic- bezier(0.4, 0, 0.2, 1) 0ms',
          pointerEvents: 'none',
        },
        '&:after': {
          transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          borderBottom: `5 px solid ${colors.bright_turquoise}`,
        },
      },
    },
  },
};

const createTheme = (): Theme => createMuiTheme(options);

export default createTheme;
